import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { NotificationToastItem } from '../types';
import { useDrawersStore } from 'src/stores/useDrawersStore';

const AUTO_DISMISS_MS = 10_000;

let queue: NotificationToastItem[] = [];
let listeners: Array<() => void> = [];

function emitChange() {
	for (const listener of listeners) {
		listener();
	}
}

function subscribe(listener: () => void) {
	listeners = [...listeners, listener];
	return () => {
		listeners = listeners.filter((l) => l !== listener);
	};
}

function getSnapshot(): NotificationToastItem[] {
	return queue;
}

function getServerSnapshot(): NotificationToastItem[] {
	return [];
}

function dismissFirst() {
	queue = queue.slice(1);
	emitChange();
}

function clearQueue() {
	if (queue.length === 0) return;
	queue = [];
	emitChange();
}

export function enqueueNotificationToast(item: NotificationToastItem) {
	const isPanelOpen = useDrawersStore.getState().notifications.show;
	if (isPanelOpen) return;
	queue = [...queue, item];
	emitChange();
}

export function useNotificationToastQueue() {
	const items = useSyncExternalStore(
		subscribe,
		getSnapshot,
		getServerSnapshot
	);
	const isPanelOpen = useDrawersStore((s) => s.notifications.show);

	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const currentToast = items[0] ?? null;

	const dismiss = useCallback(() => {
		dismissFirst();
	}, []);

	// Auto-dismiss timer
	useEffect(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}

		if (currentToast) {
			timerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);
		}

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, [currentToast, dismiss]);

	// Clear queue when panel opens
	useEffect(() => {
		if (isPanelOpen) {
			clearQueue();
		}
	}, [isPanelOpen]);

	return { currentToast, enqueue: enqueueNotificationToast, dismiss, clearAll: clearQueue };
}
